'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';

import { usePageActionsSlotContext } from './PageActionsSlot';

type UsePageRefreshOptions = {
  onRefresh: () => void | Promise<void>;
  disabled?: boolean;
  isRefreshing?: boolean;
  ariaLabel?: string;
};

export const usePageRefresh = ({ onRefresh, disabled, isRefreshing, ariaLabel }: UsePageRefreshOptions): void => {
  const { registerRefresh, unregisterRefresh } = usePageActionsSlotContext();
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) {
    idRef.current = Symbol('pageRefresh');
  }

  const tCommon = useTranslations('common');
  const label = ariaLabel ?? tCommon('actions.refresh');
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const handleRefreshClick = useCallback(() => {
    void onRefreshRef.current();
  }, []);

  const node = useMemo<ReactNode>(
    () => (
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={handleRefreshClick}
        disabled={disabled || isRefreshing}
        className="btn btn-ghost btn-sm btn-circle hover:bg-transparent active:bg-transparent focus:bg-transparent disabled:bg-transparent focus-visible:outline-none focus-visible:ring-0 [-webkit-tap-highlight-color:transparent]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
          />
        </svg>
      </button>
    ),
    [label, handleRefreshClick, disabled, isRefreshing],
  );

  useEffect(() => {
    const id = idRef.current!;
    registerRefresh(id, node);
    return () => {
      unregisterRefresh(id);
    };
  }, [node, registerRefresh, unregisterRefresh]);
};
